import React, { useEffect, useState } from 'react';
import { Search, Plus, Edit, Trash2, Package, FileSpreadsheet } from 'lucide-react';
import { productsApi, categoriesApi, settingsApi } from '../api';
import { formatPrice } from '../store';
import { Modal } from '../components/ui/Modal';
import { StatusBadge, StatusTone } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { Pagination, DEFAULT_PAGE_SIZE } from '../components/ui/Pagination';
import type { ProductListItem, Category } from '../types';
import { BulkUploadModal } from './BulkUploadModal';
import { useAuthStore } from '../store/auth';

interface ProductFormData {
  name: string;
  sku: string;
  category: number | '';
  barcode: string;
  cost_amount: string;
  cost_currency: 'USD' | 'UZS';
  sell_price_uzs: string;
  stock: number;
  low_stock_threshold: number;
  description: string;
  is_active: boolean;
}

const initialFormData: ProductFormData = {
  name: '',
  sku: '',
  category: '',
  barcode: '',
  cost_amount: '',
  cost_currency: 'USD',
  sell_price_uzs: '',
  stock: 0,
  low_stock_threshold: 5,
  description: '',
  is_active: true,
};

const stockStatus = (product: ProductListItem): { tone: StatusTone; label: string } => {
  if (product.stock === 0) return { tone: 'danger', label: `${product.stock} ta` };
  if (product.stock <= 5) return { tone: 'warning', label: `${product.stock} ta` };
  return { tone: 'success', label: `${product.stock} ta` };
};

export const Products: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const canSeeCost = useAuthStore((s) => s.permissions.can_see_cost);
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  // Artikul/shtrix kod avtomatik beriladi; sotuvchi xohlasa qo'lda kiritadi
  const [autoCodes, setAutoCodes] = useState({ sku: true, barcode: true });
  const [generatedCodes, setGeneratedCodes] = useState({ sku: '', barcode: '' });
  const [codesLoading, setCodesLoading] = useState(false);
  // Foyda ko'rsatkichi uchun do'kon kursi (tan narx dollarda kiritilsa kerak bo'ladi)
  const [settingsRate, setSettingsRate] = useState<string>('0');

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      if (selectedCategory) params.category = selectedCategory;

      const res = await productsApi.list(params);
      const rawData = res.data as any;
      const data = Array.isArray(rawData) ? rawData : (rawData.results || []);
      const count = Array.isArray(rawData) ? rawData.length : (rawData.count || 0);
      setProducts(data);
      setTotal(count);
    } catch (error: any) {
      // Oxirgi sahifadagi yagona tovar o'chirilsa DRF 404 qaytaradi —
      // bunday holda oldingi sahifaga qaytamiz.
      if (error.response?.status === 404 && page > 1) {
        setPage(page - 1);
        return;
      }
      console.error('Products fetch error:', error);
      toast.error('Tovarlarni yuklashda xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    categoriesApi.list({ page_size: 100 }).then((res) => {
      const rawData = res.data as any;
      const data = Array.isArray(rawData) ? rawData : (rawData.results || []);
      setCategories(data);
    }).catch(() => setCategories([]));
    settingsApi.get()
      .then((res) => setSettingsRate(res.data.usd_rate))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, selectedCategory]);

  // Qidiruv debounce — dastlabki mount'da ishlamaydi (aks holda ikki marta yuklanadi)
  const searchMounted = React.useRef(false);
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchProducts();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadNextCodes = async () => {
    setCodesLoading(true);
    try {
      const res = await productsApi.nextCodes();
      setGeneratedCodes(res.data);
      // Kod kelguncha sotuvchi o'zi yozib ulgurgan bo'lsa — ustidan yozmaymiz
      setFormData((prev) => ({
        ...prev,
        sku: prev.sku || res.data.sku,
        barcode: prev.barcode || res.data.barcode,
      }));
    } catch (error) {
      console.error('Next codes error:', error);
      setAutoCodes({ sku: false, barcode: false });
    } finally {
      setCodesLoading(false);
    }
  };

  // Avtomatik ↔ qo'lda rejimini almashtirish
  const toggleAutoCode = (field: 'sku' | 'barcode') => {
    const nextAuto = !autoCodes[field];
    setAutoCodes((prev) => ({ ...prev, [field]: nextAuto }));
    setFormData((prev) => ({ ...prev, [field]: nextAuto ? generatedCodes[field] : '' }));
    if (!nextAuto) {
      // Qo'lda kiritishga o'tdi — kursorni darhol shu maydonga qo'yamiz
      requestAnimationFrame(() => document.getElementById(`product-${field}`)?.focus());
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setAutoCodes({ sku: true, barcode: true });
    setGeneratedCodes({ sku: '', barcode: '' });
    setShowModal(true);
    loadNextCodes();
  };

  const openEditModal = async (id: number) => {
    try {
      const res = await productsApi.get(id);
      const p = res.data;
      setFormData({
        name: p.name,
        sku: p.sku,
        category: p.category || '',
        barcode: p.barcode || '',
        cost_amount: p.cost_amount,
        cost_currency: p.cost_currency,
        sell_price_uzs: String(p.sell_price_uzs ?? ''),
        stock: p.stock,
        low_stock_threshold: p.low_stock_threshold,
        description: p.description || '',
        is_active: p.is_active,
      });
      setAutoCodes({ sku: false, barcode: false });
      setGeneratedCodes({ sku: p.sku, barcode: p.barcode || '' });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Tovarni yuklashda xato');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialFormData);
    setAutoCodes({ sku: true, barcode: true });
    setGeneratedCodes({ sku: '', barcode: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        category: formData.category || undefined,
      };

      if (editingId) {
        await productsApi.update(editingId, payload as any);
        toast.success('Tovar yangilandi');
      } else {
        await productsApi.create(payload as any);
        toast.success('Tovar qo\'shildi');
      }
      closeModal();
      fetchProducts();
    } catch (error: any) {
      console.error('Save error:', error);
      const msg = error.response?.data?.detail || 'Saqlashda xato yuz berdi';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    const confirmed = await confirm({
      title: "Tovarni o'chirish",
      description: `"${name}" tovarini o'chirmoqchimisiz? Bu amalni ortga qaytarib bo'lmaydi.`,
      confirmLabel: "O'chirish",
      danger: true,
    });
    if (!confirmed) return;

    setDeleting(id);
    try {
      await productsApi.delete(id);
      toast.success("Tovar o'chirildi");
      fetchProducts();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("O'chirishda xato yuz berdi");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tovarlar</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowBulkUpload(true)} className="btn btn-secondary flex items-center gap-2">
            <FileSpreadsheet size={20} />
            <span className="hidden sm:inline">Excel'dan yuklash</span>
            <span className="sm:hidden">Excel</span>
          </button>
          <button onClick={openAddModal} className="btn btn-primary flex items-center gap-2">
            <Plus size={20} />
            <span className="hidden sm:inline">Yangi tovar</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              /* Uzunroq matn 360px ekranda yopuvchi qavsigacha kesilib qolardi. */
              placeholder="Nom, artikul yoki shtrix kod"
              className="input pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="input md:w-48"
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value ? Number(e.target.value) : '');
              setPage(1);
            }}
          >
            <option value="">Barcha kategoriyalar</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.full_path || cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Products list */}
      <div className="card overflow-hidden !p-0">
        {loading ? (
          <SkeletonRows />
        ) : products.length === 0 ? (
          <EmptyState icon={Package} title="Tovar topilmadi" />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Artikul</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nomi</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Kategoriya</th>
                    {canSeeCost && (
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Asl narx</th>
                    )}
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Sotish narxi</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qoldiq</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amallar</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const stock = stockStatus(product);
                    return (
                      <tr key={product.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm">{product.sku}</td>
                        <td className="py-3 px-4">
                          <div className="font-medium">{product.name}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-500">{product.category_name}</td>
                        {canSeeCost && (
                          <td className="py-3 px-4 text-right text-gray-500 whitespace-nowrap">
                            {product.cost_uzs != null ? `${formatPrice(product.cost_uzs)} UZS` : '-'}
                          </td>
                        )}
                        <td className="py-3 px-4 text-right font-medium whitespace-nowrap">
                          {formatPrice(product.sell_price_uzs)} UZS
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center">
                            <StatusBadge tone={stock.tone}>{stock.label}</StatusBadge>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center gap-1">
                            <button
                              onClick={() => openEditModal(product.id)}
                              className="p-2 text-primary-800 hover:bg-primary-50 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(product.id, product.name)}
                              disabled={deleting === product.id}
                              className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-gray-100">
              {products.map((product) => {
                const stock = stockStatus(product);
                return (
                  <div key={product.id} className="p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">{product.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{product.sku} • {product.category_name}</div>
                      {/* Narx va qoldiq o'rtasidan bo'linmasin; joy yetmasa
                          tan narx tushib qolsin, summa esa butun qolsin. */}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-semibold text-sm whitespace-nowrap">
                          {formatPrice(product.sell_price_uzs)} UZS
                        </span>
                        {canSeeCost && product.cost_uzs != null && (
                          /* Birlik yozilmaydi — yonidagi sotish narxida allaqachon bor,
                             va "UZS" bilan birga qator sig'may kesilib qolardi. */
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            ({formatPrice(product.cost_uzs)})
                          </span>
                        )}
                        <StatusBadge tone={stock.tone}>{stock.label}</StatusBadge>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditModal(product.id)}
                        className="p-2 text-primary-800 hover:bg-primary-50 rounded-lg"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.id, product.name)}
                        disabled={deleting === product.id}
                        className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg disabled:opacity-50"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            count={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editingId ? 'Tovarni tahrirlash' : 'Yangi tovar'}
        footer={
          <>
            <button type="button" onClick={closeModal} className="btn btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" form="product-form" disabled={saving} className="btn btn-primary">
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </>
        }
      >
        <form id="product-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Nomi *</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {([
              { field: 'sku', label: 'Artikul (SKU)', placeholder: 'Masalan: Panda-424' },
              { field: 'barcode', label: 'Shtrix kod', placeholder: 'Masalan: 2000000000015' },
            ] as const).map(({ field, label, placeholder }) => (
              <div key={field}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="label mb-0" htmlFor={`product-${field}`}>{label}</label>
                  {!editingId && (
                    <button
                      type="button"
                      onClick={() => toggleAutoCode(field)}
                      className="text-xs font-medium text-primary-800 hover:underline"
                    >
                      {autoCodes[field] ? "Qo'lda kiritish" : 'Avtomatik'}
                    </button>
                  )}
                </div>
                <input
                  id={`product-${field}`}
                  type="text"
                  className={`input ${autoCodes[field] ? 'bg-gray-50 text-gray-500' : ''}`}
                  value={formData[field]}
                  readOnly={autoCodes[field]}
                  placeholder={autoCodes[field] && codesLoading ? 'Yaratilmoqda...' : placeholder}
                  onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                />
                {autoCodes[field] && (
                  <p className="mt-1 text-xs text-gray-500">Avtomatik beriladi</p>
                )}
              </div>
            ))}
          </div>

          <div>
            <label className="label">Kategoriya</label>
            <select
              className="input"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value ? Number(e.target.value) : '' })}
            >
              <option value="">Tanlang...</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.full_path || cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="label">Tan narxi *</label>
              <input
                type="number"
                step="0.01"
                className="input"
                value={formData.cost_amount}
                onChange={(e) => setFormData({ ...formData, cost_amount: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label">Valyuta</label>
              <select
                className="input"
                value={formData.cost_currency}
                onChange={(e) => setFormData({ ...formData, cost_currency: e.target.value as 'USD' | 'UZS' })}
              >
                <option value="USD">Dollar ($)</option>
                <option value="UZS">So'm</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">Sotish narxi (so'm) *</label>
            <input
              type="number"
              step="1"
              className="input"
              value={formData.sell_price_uzs}
              onChange={(e) => setFormData({ ...formData, sell_price_uzs: e.target.value })}
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              O'zingiz belgilaysiz. Kerak bo'lsa tan narxiga teng qo'yishingiz ham mumkin.
              {formData.cost_amount && formData.sell_price_uzs && (() => {
                const cost = formData.cost_currency === 'UZS'
                  ? Number(formData.cost_amount)
                  : Number(formData.cost_amount) * Number(settingsRate || 0);
                const profit = Number(formData.sell_price_uzs) - cost;
                if (!cost) return null;
                return (
                  <span className={profit < 0 ? 'text-danger-600 font-medium' : 'text-success-600 font-medium'}>
                    {' '}Foyda: {profit.toLocaleString('uz-UZ')} so'm
                  </span>
                );
              })()}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Omborda</label>
              <input
                type="number"
                className="input"
                value={formData.stock}
                onChange={(e) => setFormData({ ...formData, stock: Number(e.target.value) })}
              />
            </div>
            <div>
              <label className="label">Kam qoldi chegarasi</label>
              <input
                type="number"
                className="input"
                value={formData.low_stock_threshold}
                onChange={(e) => setFormData({ ...formData, low_stock_threshold: Number(e.target.value) })}
              />
            </div>
          </div>

          <div>
            <label className="label">Tavsif</label>
            <textarea
              className="input"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Faol</span>
          </label>
        </form>
      </Modal>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUploadModal
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => {
            setPage(1);
            fetchProducts();
          }}
        />
      )}
    </div>
  );
};
